import {
  BookOpen,
  Building2,
  Clock,
  Gift,
  Inbox,
  Mail,
  UserRoundX,
  Users,
  type LucideIcon,
} from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAdminStats } from "@/features/admin/admin";
import { ActivitySection } from "@/pages/admin/ActivitySection";
import { DashboardSkeleton } from "@/components/Skeletons";
import { sectionTitleClass } from "@/lib/typography";
import { timeAgo } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

const stagger = (i: number) => ({ "--stagger": i }) as CSSProperties;

function Stat({
  icon: Icon,
  label,
  value,
  hint,
  index,
  to,
}: {
  icon: LucideIcon;
  label: string;
  value: number;
  hint?: string;
  index: number;
  to?: string;
}) {
  const card = (
    <Card
      className={`animate-item-enter h-full ${
        to
          ? "ease-out-quart hover:ring-foreground/20 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
          : ""
      }`}
      style={stagger(index)}
    >
      <CardHeader>
        <Icon className="text-muted-foreground size-4" />
        <p className="mt-1 text-3xl font-semibold tabular-nums">{value}</p>
        <CardDescription>{label}</CardDescription>
        {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
      </CardHeader>
    </Card>
  );
  return to ? (
    <Link to={to} className="block">
      {card}
    </Link>
  ) : (
    card
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className={sectionTitleClass}>{title}</h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}

export default function DashboardPage() {
  const { data, isPending, error } = useAdminStats();

  if (isPending) return <DashboardSkeleton />;
  if (error) return <p className="text-destructive text-sm">{error.message}</p>;
  if (!data) return null;

  const { members, queues, content } = data;
  const openQueues =
    queues.pending_applications +
    queues.pending_resources +
    queues.pending_discussions;

  return (
    <div className="animate-page-enter max-w-5xl">
      <div>
        <h1 className="text-2xl">Admin</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          {members.active} active members ·{" "}
          {openQueues === 0
            ? "nothing waiting for review"
            : `${openQueues} item${openQueues === 1 ? "" : "s"} waiting for review`}
        </p>
      </div>

      {/* First, because it is the only section that is work rather than
          information. Todd, after the 1 September demo: "ideally anything
          needing action is at the top (pending applications, pending
          resources, pending legacies)." Legacies were missing from it
          entirely and showed only as a nav badge. */}
      <Section title="Waiting for you">
        <div className="grid gap-4 sm:grid-cols-3">
          <Stat
            icon={Inbox}
            label="Applications"
            value={queues.pending_applications}
            index={0}
            to="/admin/members"
          />
          <Stat
            icon={Building2}
            label="Organizations"
            value={queues.pending_organizations ?? 0}
            index={1}
            to="/admin/organizations"
          />
          <Stat
            icon={BookOpen}
            label="Resource suggestions"
            value={queues.pending_resources}
            index={2}
            to="/admin/resources"
          />
        </div>
      </Section>

      <Section title="Membership">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat icon={Users} label="Active members" value={members.active} index={0} />
          <Stat
            icon={Users}
            label="Paying"
            value={members.paid}
            hint="Counted as revenue"
            index={1}
          />
          <Stat
            icon={Gift}
            label="Gifted"
            value={members.gift}
            hint="Full access, never charged"
            index={2}
          />
          <Stat
            icon={Users}
            label="Free"
            value={members.free}
            hint="Preview only"
            index={3}
          />
          <Stat
            icon={Clock}
            label="Awaiting payment"
            value={members.pending_payment}
            index={4}
          />
          <Stat icon={UserRoundX} label="Lapsed" value={members.lapsed} index={5} />
          <Stat icon={Users} label="Total records" value={members.total} index={6} />
        </div>
      </Section>

      <Section title="Content & email">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat
            icon={BookOpen}
            label="Published resources"
            value={content.published_resources}
            index={10}
          />
          {/* "Published discussions" stood here until 2026-09-01. The in-app
              board went out with Moderation on 25 August, so it counted a
              feature that no longer exists and read zero for ever. */}
          <Stat icon={Mail} label="Emails sent" value={content.emails_sent} index={11} />
        </div>
      </Section>

      {(data.top_contributors.length > 0 || data.active_threads.length > 0) && (
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {data.top_contributors.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className={sectionTitleClass}>Top contributors</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.top_contributors.map((c) => (
                  <div key={c.member_id} className="flex items-center gap-2 text-sm">
                    <Link
                      to={`/directory/${c.member_id}`}
                      className="underline-offset-4 hover:underline"
                    >
                      {c.name}
                    </Link>
                    <Badge variant="secondary" className="text-xs">
                      {c.count}
                    </Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {data.active_threads.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className={sectionTitleClass}>Active discussions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.active_threads.map((t) => (
                  <div key={t.id} className="text-sm">
                    <Link
                      to={`/discussions/${t.id}`}
                      className="underline-offset-4 hover:underline"
                    >
                      {t.title}
                    </Link>
                    <span className="text-muted-foreground">
                      {" "}
                      · {t.reply_count} replies · {timeAgo(t.last_activity_at)}
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <ActivitySection />
    </div>
  );
}
